<!--
  @fileoverview Badge Component - Unread Message Counter (Vue 3.5)

  Displays a notification badge with unread message count on the launcher button.
  Now uses Vue 3.5 reactive props instead of DOM manipulation.

  @component Badge
  @displayName Notification Badge

  Features:
  - Animated pulse effect on new messages
  - Sonar ring animation
  - RTL layout support
  - Dynamic count display via reactive props

  @vue3.5migration - COMPLETED
  - Refactored from DOM manipulation to reactive props
  - Uses proper Vue data binding for badge count
  - Removed external DOM manipulation dependency
-->
<template>
  <!-- Badge container with reactive content -->
  <div v-if="count > 0" id="keyreply-badge">
    <div class="keyreply-badge-num">{{ count }}</div>
  </div>
</template>

<script setup lang="ts">
/**
 * @module components/Badge
 * @description Notification badge component (Vue 3.5 Composition API)
 *
 * Modernized to use Vue's reactive system instead of DOM manipulation
 */

/**
 * Component props
 */
interface Props {
  /** Number of unread messages to display */
  count?: number
}

const props = withDefaults(defineProps<Props>(), {
  count: 0,
})

// Component name for devtools
defineOptions({
  name: 'Badge',
})
</script>

<style scoped>
/*@noflip*/
html[dir="ltr"] #keyreply-badge {
  position: absolute;
  top: -2px;
  left: -13px;
}
/*@noflip*/
html[dir="rtl"] #keyreply-badge {
  position: absolute;
  top: -2px;
  right: -13px;
}
/*@noflip*/
html[dir="ltr"] .keyreply-badge-num {
  padding-top: 1.5px;
  padding-right: 1.9px;
  box-sizing: border-box;
  font-family: "Trebuchet MS", sans-serif;
  background: #ff0000;
  cursor: default;
  border-radius: 50%;
  color: #fff;
  font-weight: bold;
  font-size: 12px;
  height: 1.8rem;
  width: 1.8rem;
  letter-spacing: -0.1rem;
  line-height: 1.55;
  margin-top: -1rem;
  margin-left: 0.1rem;
  border: 0.2rem solid #fff;
  text-align: center;
  display: inline-block;
  box-shadow: 1px 1px 5px rgba(0, 0, 0, 0.2);
  animation: pulse 1.5s 1;
}
/*@noflip*/
html[dir="rtl"] .keyreply-badge-num {
  padding-top: 1.5px;
  padding-left: 1.9px;
  box-sizing: border-box;
  font-family: "Trebuchet MS", sans-serif;
  background: #ff0000;
  cursor: default;
  border-radius: 50%;
  color: #fff;
  font-weight: bold;
  font-size: 12px;
  height: 1.8rem;
  width: 1.8rem;
  letter-spacing: -0.1rem;
  line-height: 1.55;
  margin-top: -1rem;
  margin-right: 0.1rem;
  border: 0.2rem solid #fff;
  text-align: center;
  display: inline-block;
  box-shadow: -1px 1px 5px rgba(0, 0, 0, 0.2);
  animation: pulse 1.5s 1;
}
/*@noflip*/
html[dir="ltr"] .keyreply-badge-num:after {
  content: "";
  position: absolute;
  top: -0.1rem;
  left: -0.1rem;
  border: 2px solid rgba(255, 0, 0, 0.5);
  opacity: 0;
  border-radius: 50%;
  width: 100%;
  height: 100%;
  animation: sonar 1.5s 1;
}
/*@noflip*/
html[dir="rtl"] .keyreply-badge-num:after {
  content: "";
  position: absolute;
  top: -0.1rem;
  right: -0.1rem;
  border: 2px solid rgba(255, 0, 0, 0.5);
  opacity: 0;
  border-radius: 50%;
  width: 100%;
  height: 100%;
  animation: sonar 1.5s 1;
}
@keyframes sonar {
  0% {
    transform: scale(0.9);
    opacity: 1;
  }
  100% {
    transform: scale(2);
    opacity: 0;
  }
}
@keyframes pulse {
  0% {
    transform: scale(1);
  }
  20% {
    transform: scale(1.4);
  }
  50% {
    transform: scale(0.9);
  }
  80% {
    transform: scale(1.2);
  }
  100% {
    transform: scale(1);
  }
}
</style>
